/**
 * TeamSystem Impact - Revoke Token Dialog
 * Conferma revoca di un token MCP.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { agentTokenApi } from '../api/agentToken.api';

interface RevokeTokenDialogProps {
  open: boolean;
  agentId: string;
  agentName: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function RevokeTokenDialog({ open, agentId, agentName, onClose, onSuccess }: RevokeTokenDialogProps) {
  const { t } = useTranslation();
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRevoke = async () => {
    setRevoking(true);
    setError(null);
    try {
      await agentTokenApi.revoke(agentId);
      onClose();
      onSuccess?.();
    } catch (err: any) {
      setError(err.message || t('settings.tokens.revokeError'));
    } finally {
      setRevoking(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('settings.tokens.revokeTitle')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <DialogContentText>
          {t('settings.tokens.revokeQuestion')} <strong>{agentName}</strong>? {t('settings.tokens.revokeConfirm')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={revoking}>
          {t('settings.tokens.cancel')}
        </Button>
        <Button onClick={handleRevoke} color="error" variant="contained" disabled={revoking}>
          {revoking ? <CircularProgress size={20} /> : t('settings.tokens.revoke')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
